import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, KeyRound, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); 
  const [loading, setLoading] = useState(false);
  
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const handleRequestCode = async (e) => {
    e.preventDefault();
    
    // 🧹 Limpiamos el correo (quitamos espacios y forzamos minúsculas)
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) return toast.error("Ingresa tu correo electrónico");
    
    setLoading(true);
    try {
      await axios.post(`${API}/public/forgot-password`, { email: cleanEmail });
      toast.success("Si el correo existe, te hemos enviado un código");
      setStep(2);
    } catch (error) {
      toast.error("Ocurrió un error al procesar la solicitud");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    // 🧹 Limpieza doble: código sin espacios y correo exacto en minúsculas
    const cleanCode = code.replace(/\s/g, "").trim();
    const cleanEmail = email.trim().toLowerCase();

    if (!cleanCode || !newPassword) return toast.error("Completa todos los campos");
    if (newPassword.length < 6) return toast.error("La contraseña debe tener al menos 6 caracteres");

    setLoading(true);
    try {
      const res = await axios.post(`${API}/public/reset-password`, {
        email: cleanEmail,
        code: cleanCode,
        new_password: newPassword
      });
      toast.success(res.data.message || "Contraseña actualizada exitosamente");
      navigate("/admin/login"); 
    } catch (error) {
      toast.error(error.response?.data?.detail || "Código inválido o expirado");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo192.png" alt="Barbershop Logo" className="w-20 h-20 object-contain rounded-xl mx-auto mb-6" />
          <h1 className="font-heading text-3xl font-bold uppercase text-white mb-2">
            Recuperar Contraseña
          </h1>
          <p className="text-zinc-400">
            {step === 1 ? "Te enviaremos un código de seguridad a tu correo" : "Ingresa el código que te enviamos y tu nueva clave"}
          </p>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 p-8 shadow-xl rounded-xl">
          {step === 1 ? (
            <form onSubmit={handleRequestCode} className="space-y-6">
              <div>
                <label className="text-xs text-zinc-500 font-bold uppercase tracking-wider mb-2 block">
                  Correo Electrónico
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input 
                    type="email" 
                    placeholder="admin@barberia.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-zinc-800 border-none text-white h-12 pl-11 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none" 
                  />
                </div>
              </div>

              <button 
                type="submit" 
                disabled={loading}
                className="w-full bg-amber-500 text-black hover:bg-amber-400 font-heading uppercase tracking-wider font-bold h-12 rounded-lg flex items-center justify-center disabled:opacity-50"
              >
                {loading ? "Enviando..." : "Enviar Código"} <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div>
                <label className="text-xs text-zinc-500 font-bold uppercase tracking-wider mb-2 block">
                  Código de 6 dígitos
                </label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input 
                    type="text" 
                    placeholder="123456"
                    maxLength="6"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="w-full bg-zinc-800 border-none text-amber-500 font-bold text-center tracking-[0.5em] text-xl h-12 pl-11 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none" 
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-zinc-500 font-bold uppercase tracking-wider mb-2 block">
                  Nueva Contraseña
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input 
                    type="password" 
                    placeholder="Mínimo 6 caracteres"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full bg-zinc-800 border-none text-white h-12 pl-11 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none" 
                  />
                </div>
              </div>

              <button 
                type="submit" 
                disabled={loading}
                className="w-full bg-amber-500 text-black hover:bg-amber-400 font-heading uppercase tracking-wider font-bold h-12 rounded-lg flex items-center justify-center disabled:opacity-50"
              >
                {loading ? "Actualizando..." : "Cambiar Contraseña"} <Check className="w-5 h-5 ml-2" />
              </button>
            </form>
          )}
        </div>

        <div className="text-center mt-6">
          <Link to="/admin/login" className="text-zinc-400 hover:text-amber-500 text-sm font-semibold flex items-center justify-center">
            <ArrowLeft className="w-4 h-4 mr-2" /> Volver al Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;